import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { resolveMutationErrorMessage } from "@/shared/api/error-message";
import { useUpdateProperty } from "./use-update-property";
import {
  MAX_DAILY_PASS_CAP,
  MAX_GEOFENCE_RADIUS,
  MAX_MAX_GUESTS,
  MIN_DAILY_PASS_CAP,
  MIN_GEOFENCE_RADIUS,
  MIN_MAX_GUESTS,
} from "./constants";
import type { PropertyDetail, UpdatePropertyInput } from "./types";

interface EditPropertyDialogProps {
  readonly property: PropertyDetail;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onSuccess: () => void;
}

/** A field value is either a valid in-range integer or an error reason. */
type FieldCheck = { readonly ok: true; readonly value: number } | { readonly ok: false };

function checkInt(raw: string, min: number, max: number): FieldCheck {
  const trimmed = raw.trim();
  if (trimmed === "") return { ok: false };
  const n = Number(trimmed);
  if (!Number.isInteger(n) || n < min || n > max) return { ok: false };
  return { ok: true, value: n };
}

/** Like checkInt but allows decimals — geofenceRadius is a Float (miles). */
function checkNumber(raw: string, min: number, max: number): FieldCheck {
  const trimmed = raw.trim();
  if (trimmed === "") return { ok: false };
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < min || n > max) return { ok: false };
  return { ok: true, value: n };
}

export function EditPropertyDialog({
  property,
  open,
  onOpenChange,
  onSuccess,
}: EditPropertyDialogProps) {
  const { t } = useTranslation();
  // No per-field RBAC: WANDERFIT_ADMIN and WANDERFIT_SUPPORT may both edit every
  // field (the detail-page Edit button + the API `@Roles(ADMIN, SUPPORT)` are the
  // gate). FINANCE never reaches this dialog. The API stays authoritative.

  const [maxGuests, setMaxGuests] = useState("");
  const [dailyPassCap, setDailyPassCap] = useState("");
  const [geofenceRadius, setGeofenceRadius] = useState("");
  const [error, setError] = useState<string | null>(null);
  const update = useUpdateProperty(property.id);

  useEffect(() => {
    if (open) {
      setMaxGuests(String(property.maxGuests));
      setDailyPassCap(String(property.dailyPassCap));
      setGeofenceRadius(String(property.geofenceRadius));
      setError(null);
    }
  }, [open, property.maxGuests, property.dailyPassCap, property.geofenceRadius]);

  const maxGuestsCheck = checkInt(maxGuests, MIN_MAX_GUESTS, MAX_MAX_GUESTS);
  const capCheck = checkInt(dailyPassCap, MIN_DAILY_PASS_CAP, MAX_DAILY_PASS_CAP);
  const geofenceCheck = checkNumber(geofenceRadius, MIN_GEOFENCE_RADIUS, MAX_GEOFENCE_RADIUS);

  // Build the patch from dirty + valid fields only.
  const patch: UpdatePropertyInput = {
    ...(maxGuestsCheck.ok && maxGuestsCheck.value !== property.maxGuests
      ? { maxGuests: maxGuestsCheck.value }
      : {}),
    ...(geofenceCheck.ok && geofenceCheck.value !== property.geofenceRadius
      ? { geofenceRadius: geofenceCheck.value }
      : {}),
    ...(capCheck.ok && capCheck.value !== property.dailyPassCap
      ? { dailyPassCap: capCheck.value }
      : {}),
  };

  const shownFieldsValid = maxGuestsCheck.ok && geofenceCheck.ok && capCheck.ok;
  const hasChanges = Object.keys(patch).length > 0;
  const isValid = shownFieldsValid && hasChanges;

  const handleClose = (next: boolean) => {
    if (!next && update.isPending) return;
    onOpenChange(next);
  };

  const handleSubmit = () => {
    if (!isValid) return;
    setError(null);
    update.mutate(patch, {
      onSuccess: () => onSuccess(),
      onError: (err) => {
        setError(
          resolveMutationErrorMessage(err, t, {
            forbiddenKey: "properties.edit.errors.forbidden",
            serverPrefixKey: "properties.edit.errors.serverPrefix",
            genericKey: "properties.edit.errors.generic",
          }),
        );
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent data-testid="edit-property-dialog">
        <DialogHeader>
          <DialogTitle>{t("properties.edit.title")}</DialogTitle>
          <DialogDescription>{t("properties.edit.description")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <p
            data-testid="property-country-readonly"
            className="font-sans text-sm text-muted-foreground"
          >
            {t("properties.edit.countryFixed", { country: property.country })}
          </p>
          <div className="space-y-1.5">
            <label
              htmlFor="edit-property-maxguests"
              className="font-sans text-sm font-medium text-foreground"
            >
              {t("properties.edit.fields.maxGuests")}
            </label>
            <Input
              id="edit-property-maxguests"
              data-testid="property-maxguests-input"
              type="number"
              inputMode="numeric"
              min={MIN_MAX_GUESTS}
              max={MAX_MAX_GUESTS}
              value={maxGuests}
              aria-invalid={!maxGuestsCheck.ok}
              className={maxGuestsCheck.ok ? undefined : "border-destructive"}
              onChange={(e) => setMaxGuests(e.target.value)}
              disabled={update.isPending}
            />
            <p
              className={
                maxGuestsCheck.ok
                  ? "font-sans text-xs text-muted-foreground"
                  : "font-sans text-xs text-destructive"
              }
            >
              {t("properties.edit.hints.maxGuests", {
                min: MIN_MAX_GUESTS,
                max: MAX_MAX_GUESTS,
              })}
            </p>
          </div>

          <div className="space-y-1.5">
            <label
              htmlFor="edit-property-geofence"
              className="font-sans text-sm font-medium text-foreground"
            >
              {t("properties.edit.fields.geofenceRadius")}
            </label>
            <Input
              id="edit-property-geofence"
              data-testid="property-geofence-input"
              type="number"
              inputMode="decimal"
              step="0.5"
              min={MIN_GEOFENCE_RADIUS}
              max={MAX_GEOFENCE_RADIUS}
              value={geofenceRadius}
              aria-invalid={!geofenceCheck.ok}
              className={geofenceCheck.ok ? undefined : "border-destructive"}
              onChange={(e) => setGeofenceRadius(e.target.value)}
              disabled={update.isPending}
            />
            <p
              className={
                geofenceCheck.ok
                  ? "font-sans text-xs text-muted-foreground"
                  : "font-sans text-xs text-destructive"
              }
            >
              {t("properties.edit.hints.geofenceRadius", {
                min: MIN_GEOFENCE_RADIUS,
                max: MAX_GEOFENCE_RADIUS,
              })}
            </p>
          </div>

          <div className="space-y-1.5">
            <label
              htmlFor="edit-property-dailypasscap"
              className="font-sans text-sm font-medium text-foreground"
            >
              {t("properties.edit.fields.dailyPassCap")}
            </label>
            <Input
              id="edit-property-dailypasscap"
              data-testid="property-dailypasscap-input"
              type="number"
              inputMode="numeric"
              min={MIN_DAILY_PASS_CAP}
              max={MAX_DAILY_PASS_CAP}
              value={dailyPassCap}
              aria-invalid={!capCheck.ok}
              className={capCheck.ok ? undefined : "border-destructive"}
              onChange={(e) => setDailyPassCap(e.target.value)}
              disabled={update.isPending}
            />
            <p
              className={
                capCheck.ok
                  ? "font-sans text-xs text-muted-foreground"
                  : "font-sans text-xs text-destructive"
              }
            >
              {t("properties.edit.hints.dailyPassCap", {
                min: MIN_DAILY_PASS_CAP,
                max: MAX_DAILY_PASS_CAP,
              })}
            </p>
          </div>

          {error && (
            <p
              data-testid="edit-property-error"
              role="alert"
              className="font-sans text-sm font-medium text-destructive"
            >
              {error}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)} disabled={update.isPending}>
            {t("properties.edit.cancel")}
          </Button>
          <Button
            data-testid="save-property-button"
            onClick={handleSubmit}
            disabled={!isValid || update.isPending}
          >
            {update.isPending ? t("properties.edit.saving") : t("properties.edit.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
